<template>
  <Dropdown
    v-model="shown"
    :disabled
    :container
    :placement
    popper-class="ui-dropdown-search-list"
    @hide="handleHide"
  >
    <template #default="{ show, hide, toggle, icon }">
      <slot :show :hide :toggle :icon />
    </template>
    <template #popper>
      <div class="ui-dropdown-search-list__container">
        <div v-if="modelValue" class="ui-dropdown-search-list__selected-value">
          <div class="ui-dropdown-search-list__selected-value-label">
            {{ modelValue }}
          </div>
          <UIButtonIcon
            :leading="XMarkIcon"
            variant="gray"
            fill="outlined"
            size="xxs"
            @click="handleClear"
          />
        </div>
        <template v-else>
          <UIInputTextClearable
            v-model="searchValue"
            placeholder="Find an option..."
            size="sm"
            :disabled
          >
            <template #leading>
              <MagnifyingGlassIcon aria-hidden="true" />
            </template>
          </UIInputTextClearable>
          <div class="ui-dropdown-search-list__list">
            <div v-if="filteredData.length === 0" class="ui-dropdown-search-list__item-empty">Nothing found</div>
            <template v-else>
              <DropdownSearchListItem
                v-for="item in filteredData"
                :key="item.value"
                :item
                @click:item="handleClickItem"
              />
            </template>
          </div>
        </template>
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Placement } from 'floating-vue'

import { DropdownListData } from './utils/types'

import Dropdown from './Dropdown.vue'
import DropdownSearchListItem from './DropdownSearchListItem.vue'
import { UIInputTextClearable } from '@ui/fields'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { UIButtonIcon } from '@ui/buttons'

type Props = {
  data: DropdownListData[]

  disabled?: boolean

  container?: string
  placement?: Placement
}

type Emits = {
  'click:item': [data?: DropdownListData['value'], event?: Event]
}

const { placement = 'bottom-end', data } = defineProps<Props>()

const emit = defineEmits<Emits>()

const modelValue = defineModel<string>('modelValue')
const shown = defineModel<boolean>('shown', { default: false })

const searchValue = ref('')

const filteredData = computed(() => {
  return data.filter(item => item.label.toLowerCase().includes(searchValue.value.toLowerCase()))
})

const handleHide = () => {
  shown.value = false
}

const handleClear = () => {
  modelValue.value = undefined
}

const handleClickItem = (value: string, event: Event) => {
  handleHide()
  emit('click:item', value, event)
}
</script>

<style>
.ui-dropdown-search-list {
  .v-popper__inner {
    @apply max-w-80 !w-[120vw];
  }

  .v-popper__arrow-container {
    @apply hidden;
  }

  &__container {
    @apply flex flex-col;
    @apply gap-1 px-1.5 py-1;
  }

  &__list {
    @apply flex flex-col;
    @apply gap-0.5;
    @apply max-h-80 overflow-y-auto;
  }

  &__item {
    @apply px-2.5 py-1.5;
    @apply rounded;
    @apply text-sm sm:text-xs;
    @apply text-[var(--text-color)];
    @apply hover:text-[var(--hover-color)];
    @apply hover:bg-[var(--hover-bg)];
    @apply cursor-pointer;
  }

  &__item-label {
    @apply truncate;
  }

  &__item-empty {
    @apply px-2.5 py-1;
    @apply text-sm sm:text-xs;
    @apply text-[var(--text-color)];
  }

  &__selected-value {
    @apply flex items-center;
    @apply gap-1 px-1;
  }

  &__selected-value-label {
    @apply flex-auto;
    @apply text-xs text-common-text;
    @apply truncate;
  }
}
</style>
